/**
 * Acesso ao `ctx.waitUntil` da request corrente, de qualquer ponto da stack.
 *
 * O PROBLEMA que isto resolve (medido em produção, 27/08/2026): o
 * `cachedLoaderCf` grava a tier persistente com um put fire-and-forget. Um put
 * pequeno (`miess/getBrand`, poucos KB) termina antes da resposta e o hit rate
 * é 98,1%. O put da PLP é de 9,6MB e NÃO termina: quando o worker devolve a
 * resposta (1,37MB de HTML), a request acaba e as promises pendentes são
 * canceladas — mais ainda com `no_handle_cross_request_promise_resolution`
 * ligado no wrangler.jsonc. Resultado: `miess/legacyProductListingPage` com
 * 2,3% de hit e HIT fresco ZERO em 3.222 chamadas, enquanto os loaders de
 * payload pequeno beiram 100%. O hit rate correlacionava inversamente com o
 * tamanho do payload — assinatura de put cancelado, não de chave errada.
 *
 * A prova cruzada: pelo `/deco/invoke` o MESMO loader de 9,6MB acerta a tier
 * persistente (4,45s → 0,83s → 0,67s, e >2MB pula o L1, então só pode ter
 * vindo da Cache API). Lá a resposta É o JSON de 9,6MB, então a request vive o
 * suficiente para o put terminar. Mesma chave, mesma Cache API: o que muda é o
 * tempo de vida da request.
 *
 * O `ctx.waitUntil` é o mecanismo da Cloudflare para exatamente isso — manter
 * trabalho vivo depois da resposta (é o que o próprio framework usa no
 * `storeInCache` do edge, workerEntry.ts, e no flush do OTel). Ele só existe no
 * `ExecutionContext` do handler; o `RequestContext` do framework não o expõe.
 * Como `src/worker-entry.ts` é nosso e recebe `(request, env, ctx)`, capturamos
 * o `waitUntil` aqui num AsyncLocalStorage e o disponibilizamos para o resto da
 * stack.
 *
 * Por que AsyncLocalStorage e não uma global: requests são concorrentes no
 * mesmo isolate; uma global entregaria o `ctx` de OUTRA request (e sob
 * `no_handle_cross_request_promise_resolution` isso é justamente o que o runtime
 * proíbe). O ALS dá o escopo correto por request. Requer `nodejs_compat`, já
 * ligado — e é o mesmo mecanismo que o `RequestContext` do framework usa.
 */
import { AsyncLocalStorage } from "node:async_hooks";
import { RequestContext } from "@decocms/blocks/sdk/requestContext";

export type WaitUntil = (promise: Promise<unknown>) => void;

const storage = new AsyncLocalStorage<WaitUntil>();

/**
 * Rota 2 de captura, por IDENTIDADE da Request. O ALS acima cobre tudo que
 * roda dentro da promise de `decoWorker.fetch()` — mas trabalho agendado
 * DEPOIS da Response retornada (stream SSR, sections deferred) pode acordar
 * fora desse escopo e cair no fallback floating em silêncio (suspeita central
 * de por que o #173 não mudou o hit rate). O framework mantém o SEU
 * AsyncLocalStorage (`RequestContext`) apontando para a MESMA Request que o
 * worker-entry recebeu; um WeakMap Request→waitUntil dá então uma segunda
 * chance de resolver o ctx correto sem risco de pegar o de outra request
 * concorrente. WeakMap: a entrada morre com a Request, sem leak.
 */
const byRequest = new WeakMap<Request, WaitUntil>();

/** Chamado pelo worker-entry, que é quem tem `ctx` e a Request em mãos. */
export function registerRequestWaitUntil(request: Request, waitUntil: WaitUntil): void {
  byRequest.set(request, waitUntil);
}

/** Qual rota resolveu o `waitUntil` — vira label de métrica no keepAlive. */
export type KeepAlivePath = "als" | "request" | "floating";

const resolveWaitUntil = (): { waitUntil: WaitUntil; path: KeepAlivePath } | undefined => {
  const fromAls = storage.getStore();
  if (fromAls) return { waitUntil: fromAls, path: "als" };
  const request = RequestContext.current?.request;
  const fromRequest = request && byRequest.get(request);
  if (fromRequest) return { waitUntil: fromRequest, path: "request" };
  return undefined;
};

/** Roda `fn` com o `waitUntil` desta request disponível para a stack inteira. */
export function runWithWaitUntil<T>(waitUntil: WaitUntil | undefined, fn: () => T): T {
  if (typeof waitUntil !== "function") return fn();
  return storage.run(waitUntil, fn);
}

/**
 * `waitUntil` da request corrente, ou `undefined` fora de escopo (testes, dev,
 * chamadas de módulo). Quem chama deve ter fallback — nunca assuma presença.
 */
export function getWaitUntil(): WaitUntil | undefined {
  return storage.getStore();
}

/**
 * Agenda trabalho de background que DEVE sobreviver à resposta (put de cache,
 * flush de métrica). Com `waitUntil` disponível, a Cloudflare mantém o worker
 * vivo até a promise resolver; sem ele, degrada para floating promise — mesmo
 * comportamento de antes, e o pior caso é a gravação se perder.
 *
 * Nunca propaga erro: trabalho de background não pode derrubar a request nem
 * gerar unhandled rejection.
 *
 * Devolve QUAL rota segurou a promise ("als" | "request" | "floating") — o
 * chamador usa isso como label de métrica, porque "floating" é exatamente o
 * modo de falha silencioso que precisamos enxergar no painel.
 */
export function keepAlive(work: () => Promise<unknown>): KeepAlivePath {
  let promise: Promise<unknown>;
  try {
    promise = work();
  } catch {
    return "floating"; // trabalho síncrono que lançou antes de virar promise
  }
  const guarded = promise.catch(() => {});
  const resolved = resolveWaitUntil();
  if (resolved) {
    try {
      resolved.waitUntil(guarded);
      return resolved.path;
    } catch {
      // ctx já encerrado — cai no floating abaixo.
    }
  }
  void guarded;
  return "floating";
}
